import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  Table,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faArrowLeft,
  faInfoCircle,
  faEye,
  faTrash,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { useForm } from "react-hook-form";
import { trackPromise } from "react-promise-tracker";
import Alerts from "../../molecules/Alerts";
import Confirm from "../../molecules/Confirm";
import { v4 as UUID } from "uuid";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import "./BankDetailsPage.css";
import {
  apiGetRequest,
  apiPostBankData,
  apiDeleteBankData,
  apiDeleteFile,
  apiGetBankDataFromIBAN,
  apiGetBankDataFromBIC,
  apiBankDataSetPrimary,
  apiRemovePrimaryBank,
  apiValidateABARoutingNumber,
  apiCalculateIBANFromRegNo,
} from "../../../utils/queries";
import { paymentTypes } from "../../../utils/constants";
import { Validate } from "../../../utils/Validator";
import { useModalCleanup } from "../../../utils/useModalCleanup";
import NewIbanForm from "./PaymentType/IbanForm";
import NonIBANForm from "./PaymentType/NonIbanForm";
import FikForm from "./PaymentType/FikForm";
import RegNoAccountForm from "./PaymentType/RegNoAccountForm";
import AbaRoutingNumberForm from "./PaymentType/AbaRoutingNumberForm";

const BankDetailsPage = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const modalCleanup = useModalCleanup();

  const [request, setRequest] = useState({});
  const [bankDataList, setBankDataList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [allCurrencyList, setAllCurrencyList] = useState([]);
  const [availableCurrencyList, setAvailableCurrencyList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [requestLoaded, setRequestLoaded] = useState(false);
  const [isFinanceAccount, setIsFinanceAccount] = useState(false);
  const [isGiro, setIsGiro] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const priorityOrder = ["USD", "SGD", "EUR", "DKK"];
  const beneficiaryPattern = /[^a-zA-Z0-9 (),.&]/g;
  const accountNumberPattern = /[^a-zA-Z0-9]/g;
  const defaultCurrency = isFinanceAccount
    ? priorityOrder[3]
    : priorityOrder[0];
  const accountNumberLength = 10;

  const [isIBAN, setIsIBAN] = useState(isFinanceAccount ? false : true);
  const [keepThisOnly, setKeepThisOnly] = useState(false);
  const [isBankAccountNumberValid, setIsBankAccountNumberValid] = useState(true);
  const [isRegNoValid, setIsRegNoValid] = useState(true);
  const [isBICValid, setIsBICValid] = useState(true);
  const [isCorrespondentBICValid, setIsCorrespondentBICValid] = useState(true);
  const [currency, setCurrency] = useState(defaultCurrency);
  const [enableSubmit, setEnableSubmit] = useState(false);
  const [isIbanValidated, setIsIbanValidated] = useState(false);
  const [isBicValidated, setIsBicValidated] = useState(false);
  const [isAbaRoutingNumberValidated, setIsAbaRoutingNumberValidated] = useState(false);
  const [isRegNoAccountValidated, setIsRegNoAccountValidated] = useState(false);

  const [correspondentBicData, setCorrespondentBicData] = useState(null);
  const [isCorrespondentBicValidated, setIsCorrespondentBicValidated] = useState(false);
  const [validatedBicCode, setValidatedBicCode] = useState("");
  const [validatedCorrespondentBicCode, setValidatedCorrespondentBicCode] = useState("");

  const [correspondentAbaData, setCorrespondentAbaData] = useState(null);
  const [isCorrespondentAbaValidated, setIsCorrespondentAbaValidated] = useState(false);
  const [validatedCorrespondentAbaCode, setValidatedCorrespondentAbaCode] = useState("");

  const [countryCode, setCountryCode] = useState("");
  const [paymentType, setPaymentType] = useState(null);
  const [showRegNo, setShowRegNo] = useState(false);
  const [regNo, setRegNo] = useState("");
  const [beneficiaryNameIban, setBeneficiaryNameIban] = useState("");
  const [beneficiaryNameSwift, setBeneficiaryNameSwift] = useState("");
  const [fikNumber, setFikNumber] = useState("");
  const [kortart, setKortart] = useState("+01");
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [abaRoutingNumber, setAbaRoutingNumber] = useState("");
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedBankData, setSelectedBankData] = useState({});
  const [toggleConfirmDelete, setToggleConfirmDelete] = useState(false);

  const { register, handleSubmit, formState: { errors }, setError, setValue, clearErrors, reset, getValues,
  } = useForm({
    defaultValues: {
      isIBAN: isIBAN,
      type: paymentType,
      currencyId: availableCurrencyList.find((c) => c.name === currency)?.id,
    },
  });

  const addAlert = (message, color, timeout = 7000) => {
    let id = UUID();
    setAlerts((prev) => [
      ...prev,
      { message: message, id: id, color: color, timeout: timeout },
    ]);
  };

  /**
   * Fetch request and bank data on component mount and set state accordingly so that the form is populated.
   */
  useEffect(() => {
    const getRequest = async () => {
      try {
        const request = await trackPromise(apiGetRequest(token));

        setRequest(request.data.request || {});
        setBankDataList(request.data.bankDataList || []);
        setFileList(request.data.files || request.data.fileList || []);
        setAllCurrencyList(request.data.currencyList || []);
        setAvailableCurrencyList(request.data.currencyList || []);
        setCountryList(request.data.countryList || []);
        setIsGiro(request.data.Request?.giro ? true : false);
        setIsFinanceAccount(request.data.AccountTypes?.includes("Finance") || false);
        setRequestLoaded(true);
      } catch (error) {
        addAlert("Error loading request. Please try again. " + error, "error");
      }
    };

    if (token) {
      getRequest();
    }
  }, [token]);


  useEffect(() => {
    const checkFormValidity = () => {
      // Early return if no payment type is selected
      if (!paymentType) {
        setEnableSubmit(false);
        return;
      }

      // Get current beneficiary name based on payment type
      const currentBeneficiaryName =
        paymentType === paymentTypes.IBAN
          ? beneficiaryNameIban
          : paymentType === paymentTypes.SWIFT
            ? beneficiaryNameSwift
            : "";

      // Validate beneficiary name is required for IBAN and SWIFT
      if (paymentType !== paymentTypes.REGNOACCOUNT &&
        paymentType !== paymentTypes.FIK &&
        paymentType !== paymentTypes.ABARoutingNumber) {
        const beneficiaryName = Validate.required(currentBeneficiaryName?.trim(), "Beneficiary Name");
        if (!beneficiaryName.isValid) {
          setEnableSubmit(false);
          return;
        }
        // Validate beneficiary name for ampersands and pattern
        const beneficiaryNameAmpersands = Validate.noAmpersands(currentBeneficiaryName, "Beneficiary Name");
        if (!beneficiaryNameAmpersands.isValid) {
          setEnableSubmit(false);
          return;
        }
        // Validate beneficiary name pattern
        const beneficiaryNamePattern = Validate.pattern(
          currentBeneficiaryName || "",
          beneficiaryPattern,
          "Invalid characters in beneficiary name"
        );
        if (!beneficiaryNamePattern.isValid) {
          setEnableSubmit(false);
          return;
        }
      }

      // Enable submit based on payment type and validations
      if (paymentType === paymentTypes.IBAN) {
        setEnableSubmit(isIbanValidated);
      } else if (paymentType === paymentTypes.SWIFT) {
        setEnableSubmit(isBicValidated);
      } else if (paymentType === paymentTypes.FIK) {
        setEnableSubmit(true);
      } else if (paymentType === paymentTypes.REGNOACCOUNT) {
        setEnableSubmit(isRegNoAccountValidated);
      } else if (paymentType === paymentTypes.ABARoutingNumber) {
        setEnableSubmit(true);
      }
    };

    checkFormValidity();
  }, [
    paymentType,
    beneficiaryNameIban,
    beneficiaryNameSwift,
    isBankAccountNumberValid,
    isBICValid,
    isBicValidated,
    isIbanValidated,
    fikNumber,
    regNo,
    bankAccountNumber,
    isRegNoAccountValidated,
  ]);

  // Update available currency list so that used currencies are excluded
  useEffect(() => {
    if (allCurrencyList.length > 0) {
      updateCurrencyList(bankDataList, allCurrencyList);
    }
  }, [bankDataList, allCurrencyList]);

  // Force USD currency when ABA Routing Number is selected
  useEffect(() => {
    if (paymentType === paymentTypes.ABARoutingNumber) {
      setCurrency("USD");
    }
  }, [paymentType]);

  const onSubmit = async (bankData) => {
    const formValues = getValues();

    // Create bank detail object with base properties
    const bankDetail = {
      type: paymentType,
      regNo: regNo,
      token: token,
      requestId: request.Id || request.id,
      keepThisOnly: keepThisOnly,
    };

    // Map values based on payment type
    if (paymentType === paymentTypes.REGNOACCOUNT) {
      bankDetail.bankAccountNumber = (formValues.bankAccountNumber || bankAccountNumber || "").replace(accountNumberPattern, "");
      bankDetail.iban = formValues.calculatedIban || "";
      bankDetail.bic = formValues.bic || "";
      bankDetail.bank = formValues.bankName || "";
      bankDetail.address = formValues.bankAddress || "";
      bankDetail.city = formValues.bankCity || "";
      bankDetail.zip = formValues.bankZip || "";
      bankDetail.country = formValues.bankCountry || "";
      bankDetail.countryCode = countryCode || "DK";

    } else if (paymentType === paymentTypes.IBAN) {
      bankDetail.bankAccountNumber = formValues.bankAccountNumber || bankAccountNumber;
      bankDetail.iban = formValues.bankAccountNumber || bankAccountNumber;
      bankDetail.beneficiaryBankAccountName = beneficiaryNameIban?.trim();
      bankDetail.bic = formValues.bic || "";
      bankDetail.correspondentBankSwift = formValues.correspondentBic || null;
      bankDetail.correspondentBankName = correspondentBicData?.institutionName || null;
      bankDetail.correspondentBankAddress = correspondentBicData?.opAddress || null;
      bankDetail.correspondentBankCity = correspondentBicData?.opCity || null;
      bankDetail.correspondentBankPostalCode = correspondentBicData?.opZipCode || null;
      bankDetail.correspondentBankCountryName = correspondentBicData?.countryName || null;
      bankDetail.correspondentBankCountryCode = correspondentBicData?.countryIso || correspondentBicData?.isoCountryCode || correspondentBicData?.countryCode || null;
      bankDetail.bank = formValues.bankName || "";
      bankDetail.branch = formValues.branch || "";
      bankDetail.address = formValues.bankAddress || "";
      bankDetail.city = formValues.bankCity || "";
      bankDetail.zip = formValues.bankZip || "";
      bankDetail.country = formValues.bankCountry || "";
      bankDetail.countryCode = countryCode;
      bankDetail.currencyId = formValues.currencyId || null;

    } else if (paymentType === paymentTypes.SWIFT) {
      bankDetail.bankAccountNumber = (formValues.bankAccountNumber || bankAccountNumber || "").replace(accountNumberPattern, "");
      bankDetail.beneficiaryBankAccountName = beneficiaryNameSwift?.trim();
      bankDetail.bic = formValues.bic || "";
      bankDetail.correspondentBankSwift = formValues.correspondentBic || null;
      bankDetail.correspondentBankName = correspondentBicData?.institutionName || null;
      bankDetail.correspondentBankAddress = correspondentBicData?.opAddress || null;
      bankDetail.correspondentBankCity = correspondentBicData?.opCity || null;
      bankDetail.correspondentBankPostalCode = correspondentBicData?.opZipCode || null;
      bankDetail.correspondentBankCountryName = correspondentBicData?.countryName || null;
      bankDetail.correspondentBankCountryCode = correspondentBicData?.countryIso || correspondentBicData?.isoCountryCode || correspondentBicData?.countryCode || null;
      bankDetail.countryCode = countryCode;
      bankDetail.bank = formValues.bankName || "";
      bankDetail.branch = formValues.branch || "";
      bankDetail.address = formValues.bankAddress || "";
      bankDetail.city = formValues.bankCity || "";
      bankDetail.zip = formValues.bankZip || "";
      bankDetail.country = formValues.bankCountry || "";
      bankDetail.currencyId = formValues.currencyId || null;

    } else if (paymentType === paymentTypes.FIK) {
      bankDetail.fikkortart = kortart;
      bankDetail.fikNumber = fikNumber;
      bankDetail.country = "Denmark";
      bankDetail.countryCode = "DK";
      const dkk = allCurrencyList.find((c) => c.name === "DKK");
      bankDetail.currencyId = dkk ? dkk.id : null;

      const firstNumber = bankDetail.fikNumber?.substring(0, 1);
      if (bankDetail.fikNumber?.length === 7 && firstNumber !== "0") {
        setFikNumber(`0${fikNumber}`);
        bankDetail.fikNumber = `+0${fikNumber}`;
      } else if (bankDetail.fikNumber) {
        bankDetail.fikNumber = `+${fikNumber}`;
      }

    } else if (paymentType === paymentTypes.ABARoutingNumber) {
      bankDetail.abaRoutingNumber = formValues.abaRoutingNumber || "";
      bankDetail.correspondentBankABARoutingNumber = formValues.correspondentAbaRoutingNumber || null;
      bankDetail.correspondentBankName = correspondentAbaData?.bankName || null;
      bankDetail.correspondentBankAddress = correspondentAbaData?.streetAddress || null;
      bankDetail.correspondentBankCity = correspondentAbaData?.city || null;
      bankDetail.correspondentBankPostalCode = correspondentAbaData?.zipCode || null;
      bankDetail.correspondentBankCountryName = correspondentAbaData?.country || null;
      bankDetail.correspondentBankCountryCode = correspondentAbaData ? "US" : null;
      bankDetail.countryCode = formValues.countryCode || "US";
      bankDetail.currencyId = formValues.currencyId || null;
      bankDetail.bankAccountNumber = (formValues.bankAccountNumber || "").replace(accountNumberPattern, "");
      bankDetail.beneficiaryBankAccountName = formValues.beneficiaryBankAccountName || "";
      bankDetail.bank = formValues.bankName || "";
      bankDetail.branch = formValues.branch || "";
      bankDetail.address = formValues.bankAddress || "";
      bankDetail.city = formValues.bankCity || "";
      bankDetail.zip = formValues.bankZip || "";
      bankDetail.country = formValues.bankCountry || "";
    }

    // Validate form before submission
    if (!validateForm(bankDetail)) {
      return;
    }

    if (bankDetail.currencyId) {
      bankDetail.currencyId = parseInt(bankDetail.currencyId, 10);
    } else {
      bankDetail.currencyId = null;
    }

    try {
      const result = await trackPromise(apiPostBankData(bankDetail, token));
      setBankDataList([...bankDataList, result.data]);

      addAlert("Bank data added successfully!", "success");

      // Reset form after successful submission
      setKeepThisOnly(false);
      setBeneficiaryNameIban("");
      setBeneficiaryNameSwift("");
      setRegNo("");
      setFikNumber("");
      setKortart("+01");
      setBankAccountNumber("");
      setCountryCode("");
      setEnableSubmit(false);
      setIsIbanValidated(false);
      setIsBicValidated(false);
      setIsAbaRoutingNumberValidated(false);
      setIsRegNoAccountValidated(false);
      setCorrespondentBicData(null);
      setIsCorrespondentBicValidated(false);
      setValidatedBicCode("");
      setValidatedCorrespondentBicCode("");
      setCorrespondentAbaData(null);
      setIsCorrespondentAbaValidated(false);
      setValidatedCorrespondentAbaCode("");
      reset();
    } catch (error) {
      console.error("Error submitting bank data:", error);
      addAlert(
        "Bank data could not be added. Please contact Hafnia. " + error,
        "error"
      );
    }
  };

  // Helper function to validate and show alert if form is invalid
  const validateForm = (bankData) => {

    const valid = (validation) => {
      if (!validation.isValid) {
        addAlert(validation.message, "error");
        return false;
      }
      return true;
    };

    if (paymentType === paymentTypes.ABARoutingNumber) {
      const usd = allCurrencyList.find(c => c.name === "USD");
      const usdAlreadyUsed = usd && bankDataList.some(b => b.currency?.id === usd.id);
      if (usdAlreadyUsed) {
        addAlert("A USD bank account already exists. Only one bank account per currency is allowed.", "error");
        return false;
      }
      const missingFields = [];
      if (!bankData.bankAccountNumber?.trim()) missingFields.push("Account Number");
      if (!bankData.beneficiaryBankAccountName?.trim()) missingFields.push("Beneficiary Name");
      if (missingFields.length > 0) {
        addAlert(`Please fill in the following required fields: ${missingFields.join(", ")}`, "error");
        return false;
      }
      return true;
    }

    if (paymentType === paymentTypes.REGNOACCOUNT) {
      const missingFields = Validate.requiredFieldsForRegnoAccount(
        bankData.regNo,
        bankData.bankAccountNumber
      );

      if (missingFields.length > 0) {
        const missingFieldsText = missingFields.join(", ");
        addAlert(`Please fill in the following required fields: ${missingFieldsText}`, "error");
        setIsRegNoValid(!missingFields.includes("Registration Number"));
        setIsBankAccountNumberValid(!missingFields.includes("Account Number"));
        return false;
      }

      const registrationNumberLength = Validate.exactLength(bankData.regNo, 4, "Registration number");
      if (!registrationNumberLength.isValid) {
        addAlert(registrationNumberLength.message, "error");
        return false;
      }

      return true;
    }

    if (paymentType === paymentTypes.FIK) {
      const missingFields = Validate.requiredFieldsForFik(fikNumber);

      if (missingFields.length > 0) {
        const missingFieldsText = missingFields.join(", ");
        addAlert(`Please fill in the following required fields: ${missingFieldsText}`, "error");
        return false;
      }

      // Check FIK number length (must be 7 or 8 characters)
      const fikValidation = Validate.validateFikLength(fikNumber);
      if (!fikValidation.isValid) {
        addAlert(fikValidation.message, "error");
        return false;
      }

      return true;
    }

    const iban = Validate.iban(paymentType, bankData.bankAccountNumber);
    if (!valid(iban)) return false;

    const swiftAccountNumber = Validate.swiftAccountNumber(paymentType, bankData.bankAccountNumber);
    if (!valid(swiftAccountNumber)) return false;

    const hyphenValidation = Validate.noHyphens(bankData.bankAccountNumber, "Account Number");
    if (!valid(hyphenValidation)) return false;


    // Validate registration number requirement
    if (showRegNo) {
      const registrationNumber = Validate.required(regNo?.trim(), "Registration Number");
      if (!valid(registrationNumber)) {
        setIsRegNoValid(false);
        return false;
      }
    }

    if (showRegNo) {
      const accountNumber = Validate.length(bankData.bankAccountNumber, accountNumberLength, "Account Number");
      if (!valid(accountNumber)) return false;
    }

    if (showRegNo && accountNumberPattern.test(bankData.bankAccountNumber) &&
      (bankData?.regNo?.length !== 4 || regNo?.length !== 4)) {
      const registrationNumber = Validate.exactLength(regNo, 4, "Registration number");
      if (!valid(registrationNumber)) return false;
    }

    // Get current beneficiary name
    const currentBeneficiaryName =
      paymentType === paymentTypes.IBAN
        ? beneficiaryNameIban
        : paymentType === paymentTypes.SWIFT
          ? beneficiaryNameSwift
          : bankData.beneficiaryBankAccountName;

    const beneficiaryName = Validate.noAmpersands(currentBeneficiaryName, "Beneficiary Name");
    if (!valid(beneficiaryName)) return false;

    const beneficiaryNamePattern = Validate.pattern(
      currentBeneficiaryName || "",
      beneficiaryPattern, "Only alphanumeric characters Abc-Xyz or 0-9 and special character (.,) are allowed by bank(s)."
    );
    if (!valid(beneficiaryNamePattern)) return false;

    // Validate that SWIFT and Correspondent BIC SWIFT are not the same
    if (paymentType === paymentTypes.SWIFT) {
      const swift = document.querySelector('input[name="bic"]')?.value?.trim();
      const correspondentBICSWIFT = document.querySelector('input[name="correspondentBic"]')?.value?.trim();

      const swiftFieldsUnique = Validate.uniqueValues(
        [Validate.normalizeBic(swift), Validate.normalizeBic(correspondentBICSWIFT)],
        ["SWIFT", "Correspondent BIC SWIFT"]
      );
      if (!swiftFieldsUnique.isValid && Validate.bicsMatchOnlyAfterNormalizing(swift, correspondentBICSWIFT)) {
        swiftFieldsUnique.message += ` "XXX" marks the primary office branch — these are effectively the same bank.`;
      }
      if (!valid(swiftFieldsUnique)) return false;
    }

    // Validate that the IBAN's beneficiary SWIFT (auto-detected, not shown to the user) and
    // Correspondent BIC SWIFT are not the same
    if (paymentType === paymentTypes.IBAN) {
      const swift = getValues("bic")?.trim();
      const correspondentBICSWIFT = document.querySelector('input[name="correspondentBic"]')?.value?.trim();

      if (correspondentBICSWIFT) {
        const swiftFieldsUnique = Validate.uniqueValues(
          [Validate.normalizeBic(swift), Validate.normalizeBic(correspondentBICSWIFT)],
          ["Beneficiary Bank SWIFT", "Correspondent BIC SWIFT"]
        );
        if (!swiftFieldsUnique.isValid) {
          // User never sees the auto-detected beneficiary SWIFT, so name the bank instead of the hidden field
          const beneficiaryBankName = getValues("bankName")?.trim();
          swiftFieldsUnique.message = `Correspondent Bank SWIFT refers to the same bank as the IBAN bank${beneficiaryBankName ? ` (${beneficiaryBankName})` : ""}. Leave blank if not applicable.`;
          if (Validate.bicsMatchOnlyAfterNormalizing(swift, correspondentBICSWIFT)) {
            swiftFieldsUnique.message += ` ("XXX" marks the primary branch — same bank.)`;
          }
        }
        if (!valid(swiftFieldsUnique)) return false;
      }
    }

    return true;
  };


  const updateCurrencyList = (bankDataList, allCurrencyList) => {
    const usedCurrencies = bankDataList
      .map((bank) => bank.currency?.id)
      .filter((id) => id);
    const availableCurrencies = allCurrencyList.filter(
      (currency) => !usedCurrencies.includes(currency.id)
    );
    setAvailableCurrencyList(availableCurrencies);
  };


  const onGetIBAN = async () => {
    try {
      setIsBankAccountNumberValid(true);
      setIsRegNoValid(true);
      setIsIbanValidated(false);
      setIsCorrespondentBICValid(true);

      const currentValues = {
        bankAccountNumber: document.querySelector('input[name="bankAccountNumber"]')?.value,
        correspondentBic: document.querySelector('input[name="correspondentBic"]')?.value,
      };

      const missingFields = Validate.requiredFieldsForIban(
        currentValues.bankAccountNumber,
        beneficiaryNameIban
      );
      if (missingFields.length > 0) {
        const missingFieldsText = missingFields.join(", ");
        addAlert(`Please fill in the following required fields before validating IBAN: ${missingFieldsText}`, "error");
        return;
      }

      if (showRegNo) {
        const registrationNumber = Validate.required(regNo?.trim(), "Registration Number");
        if (!registrationNumber.isValid) {
          addAlert(registrationNumber.message, "error");
          setIsRegNoValid(false);
          return;
        }
      }

      const iban = Validate.noHyphens(currentValues.bankAccountNumber, "IBAN Account Number");
      if (!iban.isValid) {
        addAlert(iban.message, "error");
        setIsBankAccountNumberValid(false);
        return;
      }

      const result = await trackPromise(apiGetBankDataFromIBAN(
        currentValues.bankAccountNumber,
        token
      ));

      setIsBankAccountNumberValid(result.data.isIBANValid);
      setIsRegNoValid(true);

      if (result.data.isIBANValid) {
        // Auto-fill all the bank data fields from IBAN validation
        const bankData = result.data.bankData;
        setValue("bic", bankData.bic);
        setValue("bankName", bankData.bank);
        setValue("branch", bankData.branch);
        setValue("bankAddress", bankData.address);
        setValue("bankCity", bankData.city);
        setValue("bankZip", bankData.zip);
        setValue("bankCountry", bankData.country);

        const countryCode = bankData.countryIso || bankData.isoCountryCode || bankData.countryCode || "";
        setCountryCode(countryCode);
        setValue("isoCountryCode", countryCode);

        // Validate correspondent BIC if provided (mirrors SWIFT behavior)
        const correspondentBicSwift = currentValues.correspondentBic?.trim();
        if (correspondentBicSwift) {
          const bicFormat = /^[A-Z0-9]{8}([A-Z0-9]{3})?$/i;
          if (!bicFormat.test(correspondentBicSwift)) {
            addAlert("Correspondent Bank SWIFT BIC must be 8 or 11 alphanumeric characters.", "error");
            setIsCorrespondentBICValid(false);
            setIsIbanValidated(false);
            setEnableSubmit(false);
            return;
          }

          const correspondentResult = await trackPromise(apiGetBankDataFromBIC(
            currentValues.bankAccountNumber,
            correspondentBicSwift,
            token
          ));

          if (!correspondentResult.data.isBICValid) {
            setIsCorrespondentBICValid(false);
            setIsIbanValidated(false);
            setEnableSubmit(false);
            addAlert("Error validating Correspondent Bank SWIFT BIC. Please enter a valid Correspondent Bank SWIFT BIC and try again.", "error");
            return;
          }

          const correspondentBicdata = correspondentResult.data.bicdata ?? {};
          setCorrespondentBicData(correspondentBicdata);
          setIsCorrespondentBicValidated(true);
          setValidatedCorrespondentBicCode(correspondentBicSwift);
          setIsIbanValidated(true);
          setEnableSubmit(true);
          addAlert("Validated IBAN Account Number and Correspondent Bank SWIFT BIC successfully. Please remember to click the Add Bank Data button.", "success");
        } else {
          setCorrespondentBicData(null);
          setIsCorrespondentBicValidated(false);
          setValidatedCorrespondentBicCode("");
          setIsIbanValidated(true);
          setEnableSubmit(true);
          addAlert("Validated IBAN Account Number successfully. Please remember to click the Add Bank Data button.", "success");
        }
      } else {
        setEnableSubmit(false);
        setIsIbanValidated(false);
        addAlert("Invalid IBAN. Please try again.", "error");
        setIsBankAccountNumberValid(false);
      }
    } catch (error) {
      console.error("Error validating IBAN:", error);
      addAlert("Error validating IBAN. Please try again.", "error");
      setIsBankAccountNumberValid(false);
      setEnableSubmit(false);
      setIsIbanValidated(false);
    }
  };

  const onGetBIC = async () => {
    try {
      setIsBankAccountNumberValid(true);
      setIsRegNoValid(true);
      setIsBICValid(true);
      setIsCorrespondentBICValid(true);

      const currentValues = {
        bankAccountNumber: document.querySelector('input[name="bankAccountNumber"]')?.value,
        bic: document.querySelector('input[name="bic"]')?.value,
        correspondentBic: document.querySelector('input[name="correspondentBic"]')?.value,
      };

      const missingFields = Validate.requiredFieldsForSwift(
        currency,
        currentValues.bankAccountNumber,
        currentValues.bic,
        beneficiaryNameSwift
      );
      if (missingFields.length > 0) {
        const missingFieldsText = missingFields.join(", ");
        addAlert(`Please fill in the following required fields before validating SWIFT: ${missingFieldsText}`, "error");
        return;
      }

      const requiredFields = Validate.swiftBicRequiredFields(
        currentValues.bankAccountNumber,
        currentValues.bic,
        regNo,
        showRegNo
      );

      if (!requiredFields.isValid) {
        if (requiredFields.invalidFields.includes("bankAccountNumber")) {
          setIsBankAccountNumberValid(false);
        }
        if (requiredFields.invalidFields.includes("regNo")) {
          setIsRegNoValid(false);
        }
        if (requiredFields.invalidFields.includes("bic")) {
          setIsBICValid(false);
        }
        return;
      }

      const accountNumber = currentValues.bankAccountNumber?.trim();
      const swift = currentValues.bic?.trim();
      const correspondentBicSwift = currentValues.correspondentBic?.trim();
      const swiftValue = Validate.swiftDifferentValues(accountNumber, swift, correspondentBicSwift);
      if (!swiftValue.isValid) {
        addAlert(swiftValue.message, "error");
        return;
      }

      if (correspondentBicSwift) {
        const bicFormat = /^[A-Z0-9]{8}([A-Z0-9]{3})?$/i;
        if (!bicFormat.test(correspondentBicSwift)) {
          addAlert("Correspondent Bank SWIFT BIC must be 8 or 11 alphanumeric characters.", "error");
          return;
        }
      }

      // --- Step 1: Validate beneficiary BIC ---
      const result = await trackPromise(apiGetBankDataFromBIC(
        currentValues.bankAccountNumber,
        swift,
        token
      ));

      if (!result.data.isBICValid) {
        setIsBICValid(false);
        setIsBicValidated(false);
        setEnableSubmit(false);
        addAlert("Error validating Beneficiary Bank SWIFT BIC. Please enter a valid Beneficiary Bank SWIFT BIC number and try again.", "error");
        return;
      }

      // Beneficiary BIC is valid — populate form fields
      const bicdata = result.data.bicdata ?? {};
      setValue("bankName", bicdata.institutionName || "");
      setValue("bankAddress", bicdata.opAddress || "");
      setValue("bankCity", bicdata.opCity || "");
      setValue("bankZip", bicdata.opZipCode || "");
      setValue("bankCountry", bicdata.countryName || "");

      const resolvedCountryCode = bicdata.countryIso || bicdata.isoCountryCode || bicdata.countryCode || "";
      setCountryCode(resolvedCountryCode);
      setValue("isoCountryCode", resolvedCountryCode);

      setIsBICValid(true);
      setValidatedBicCode(swift);

      // --- Step 2: Validate correspondent BIC if provided ---
      if (correspondentBicSwift) {
        const correspondentResult = await trackPromise(apiGetBankDataFromBIC(
          currentValues.bankAccountNumber,
          correspondentBicSwift,
          token
        ));

        if (!correspondentResult.data.isBICValid) {
          setIsCorrespondentBICValid(false);
          setIsBicValidated(false);
          setEnableSubmit(false);
          addAlert("Error validating Correspondent Bank SWIFT BIC. Please enter a valid Correspondent Bank SWIFT BIC and try again.", "error");
          return;
        }

        const correspondentBicdata = correspondentResult.data.bicdata ?? {};
        setCorrespondentBicData(correspondentBicdata);
        setIsCorrespondentBicValidated(true);
        setValidatedCorrespondentBicCode(correspondentBicSwift);

        setIsBicValidated(true);
        setEnableSubmit(true);
        addAlert("Validated Beneficiary Bank SWIFT BIC and Correspondent Bank SWIFT BIC successfully. Please remember to click the Add Bank Data button", "success");
      } else {
        // No correspondent BIC provided — clear any previous correspondent data
        setCorrespondentBicData(null);
        setIsCorrespondentBicValidated(false);
        setValidatedCorrespondentBicCode("");

        setIsBicValidated(true);
        setEnableSubmit(true);
        addAlert("Validated Beneficiary Bank SWIFT BIC successfully. Please remember to click the Add Bank Data button", "success");
      }
    } catch (error) {
      console.error("Error validating SWIFT:", error);
      addAlert("Error validating SWIFT. Please try again.", "error");
      setIsBICValid(false);
      setIsBicValidated(false);
      setEnableSubmit(false);
    }
  };

  const onGetABARoutingNumber = async () => {
    try {
      setIsAbaRoutingNumberValidated(false);
      setEnableSubmit(false);

      // Get the ABA routing number from form
      const abaRoutingNumber = document.querySelector('input[name="abaRoutingNumber"]')?.value?.trim();

      if (!abaRoutingNumber) {
        addAlert("Please enter an ABA Routing Number before validating.", "error");
        return;
      }

      if (abaRoutingNumber.length !== 9) {
        addAlert("ABA Routing Number must be exactly 9 digits.", "error");
        return;
      }

      const result = await trackPromise(apiValidateABARoutingNumber(abaRoutingNumber, token));

      if (result.data && result.data.isABARoutingNumberValid) {
        // Auto-fill bank data fields from ABA validation
        const bankData = result.data.bankData;
        setValue("bankName", bankData.bankName || "");
        setValue("bankAddress", bankData.streetAddress || "");
        setValue("bankCity", bankData.city || "");
        setValue("state", bankData.state || "");
        setValue("county", bankData.county || "");
        setValue("bankZip", bankData.zipCode || "");
        setValue("bankCountry", bankData.country || "");
        setValue("timezone", bankData.timezone || "");

        setIsAbaRoutingNumberValidated(true);

        // Validate correspondent ABA routing number if provided
        const correspondentAba = document.querySelector('input[name="correspondentAbaRoutingNumber"]')?.value?.trim();
        if (correspondentAba) {
          if (correspondentAba.length !== 9) {
            addAlert("Correspondent Bank ABA Routing Number must be exactly 9 digits.", "error");
            setEnableSubmit(false);
            return;
          }
          if (correspondentAba === abaRoutingNumber) {
            addAlert("Correspondent Bank ABA Routing Number must be different from the primary ABA Routing Number.", "error");
            setEnableSubmit(false);
            return;
          }
          const corrResult = await trackPromise(apiValidateABARoutingNumber(correspondentAba, token));
          if (corrResult.data && corrResult.data.isABARoutingNumberValid) {
            setCorrespondentAbaData(corrResult.data.bankData);
            setIsCorrespondentAbaValidated(true);
            setValidatedCorrespondentAbaCode(correspondentAba);
            setEnableSubmit(true);
            addAlert("ABA Routing Number and Correspondent Bank ABA Routing Number validated successfully. Please remember to click the Add Bank Data button.", "success");
          } else {
            addAlert("Invalid Correspondent Bank ABA Routing Number. Please try again.", "error");
            setCorrespondentAbaData(null);
            setIsCorrespondentAbaValidated(false);
            setValidatedCorrespondentAbaCode("");
            setEnableSubmit(false);
          }
        } else {
          setCorrespondentAbaData(null);
          setIsCorrespondentAbaValidated(false);
          setValidatedCorrespondentAbaCode("");
          setEnableSubmit(true);
          addAlert("ABA Routing Number validated successfully. Please remember to click the Add Bank Data button.", "success");
        }
      } else {
        addAlert("Invalid ABA Routing Number. Please try again.", "error");
        setIsAbaRoutingNumberValidated(false);
        setEnableSubmit(false);
      }
    } catch (error) {
      console.error("Error validating ABA Routing Number:", error);
      addAlert("Error validating ABA Routing Number. Please try again.", "error");
      setIsAbaRoutingNumberValidated(false);
      setEnableSubmit(false);
    }
  };

  const onCalculateIBANFromRegNo = async () => {
    try {
      setIsRegNoAccountValidated(false);
      setEnableSubmit(false);

      const registrationNumber = regNo?.trim();
      const accountNo = document.querySelector('input[name="bankAccountNumber"]')?.value?.trim();

      if (!registrationNumber) {
        addAlert("Please enter a Registration Number before validating.", "error");
        setIsRegNoValid(false);
        return;
      }

      if (registrationNumber.length !== 4) {
        addAlert("Registration Number must be exactly 4 characters.", "error");
        setIsRegNoValid(false);
        return;
      }

      if (!accountNo) {
        addAlert("Please enter an Account Number before validating.", "error");
        setIsBankAccountNumberValid(false);
        return;
      }

      const result = await trackPromise(apiCalculateIBANFromRegNo(registrationNumber, accountNo, token));

      if (result.data.isIBANValid) {
        const bankData = result.data.bankData;
        setValue("calculatedIban", bankData.iban || "");
        setValue("bic", bankData.bic || "");
        setValue("bankName", bankData.bank || "");
        setValue("bankAddress", bankData.address || "");
        setValue("bankCity", bankData.city || "");
        setValue("bankZip", bankData.zip || "");
        setValue("bankCountry", bankData.country || "");
        const isoCode = bankData.country_iso || bankData.countryIso || "DK";
        setCountryCode(isoCode);

        setIsRegNoAccountValidated(true);
        setEnableSubmit(true);
        addAlert("Account validated successfully. Please remember to click the Add Bank Data button.", "success");
      } else {
        addAlert(result.data.message || "Could not validate account. Please check the registration and account numbers.", "error");
        setIsRegNoAccountValidated(false);
        setEnableSubmit(false);
      }
    } catch (error) {
      console.error("Error calculating IBAN from RegNo/Account:", error);
      addAlert("Error validating account. Please try again.", "error");
      setIsRegNoAccountValidated(false);
      setEnableSubmit(false);
    }
  };

  const resetMetadata = () => {
    setEnableSubmit(false);
    setIsBICValid(true);
    setIsCorrespondentBICValid(true);
    setIsIbanValidated(false);
    setIsBicValidated(false);
    setIsAbaRoutingNumberValidated(false);
    setIsRegNoAccountValidated(false);
    setCorrespondentBicData(null);
    setIsCorrespondentBicValidated(false);
    setValidatedBicCode("");
    setValidatedCorrespondentBicCode("");
    setCorrespondentAbaData(null);
    setIsCorrespondentAbaValidated(false);
    setValidatedCorrespondentAbaCode("");
  };

  const resetFormFields = () => {
    // Clear all form fields
    setRegNo("");
    setBankAccountNumber("");
    setBeneficiaryNameIban("");
    setBeneficiaryNameSwift("");
    setFikNumber("");
    setKortart("+01");
    setCountryCode("");

    // Reset validation states
    setIsBankAccountNumberValid(true);
    setIsRegNoValid(true);
    setIsBICValid(true);
    setIsCorrespondentBICValid(true);
    setIsIbanValidated(false);
    setIsBicValidated(false);
    setIsAbaRoutingNumberValidated(false);
    setIsRegNoAccountValidated(false);
    setCorrespondentBicData(null);
    setIsCorrespondentBicValidated(false);
    setValidatedBicCode("");
    setValidatedCorrespondentBicCode("");
    setCorrespondentAbaData(null);
    setIsCorrespondentAbaValidated(false);
    setValidatedCorrespondentAbaCode("");
    setEnableSubmit(false);

    // Reset react-hook-form fields
    reset({
      type: paymentType,
      currencyId: availableCurrencyList.find((c) => c.name === currency)?.id,
    });

    clearErrors();
  };



  const handleKortartChange = (e) => {
    setKortart(e.target.value);
  };

  const handleOnChangeFik = (e) => {
    setFikNumber(e.target.value);
  };

  const handleOnChangeRegistrationNo = (e) => {
    setRegNo(e.target.value);
    setIsRegNoValid(e.target.value.trim() !== "");
    setIsRegNoAccountValidated(false);
  };

  const handleAccountNo = (e) => {
    const raw = e.target.value || "";
    const hasHyphen = raw.indexOf("-") !== -1;
    if (hasHyphen) {
      setIsBankAccountNumberValid(false);
    } else {
      clearErrors && clearErrors("bankAccountNumber");
    }
    const value = raw.toString().replace(accountNumberPattern, "");
    setBankAccountNumber(value);
    if (!hasHyphen) {
      setIsBankAccountNumberValid(value.trim() !== "");
    }
    setIsRegNoAccountValidated(false);
    setIsBicValidated(false);
    setIsCorrespondentBicValidated(false);
    setCorrespondentBicData(null);
    setValidatedBicCode("");
    setValidatedCorrespondentBicCode("");
  };

  const handleAbaRoutingNumberChange = (e) => {
    const value = e.target.value || "";
    // Only allow numeric characters for ABA routing number
    const numericValue = value.replace(/[^0-9]/g, "");
    setAbaRoutingNumber(numericValue);
  };

  const onBeneficiaryNameChangeIban = (e) => {
    const input = e.target.value || "";
    const value = input.toString().replace(beneficiaryPattern, "");
    e.target.value = value;
    setBeneficiaryNameIban(value);
    resetMetadata();
  };

  const onBeneficiaryNameChangeSwift = (e) => {
    const input = e.target.value || "";
    const value = input.toString().replace(beneficiaryPattern, "");
    e.target.value = value;
    setBeneficiaryNameSwift(value);
    resetMetadata();
  };

  const onCurrencyChanged = (e) => {
    // Prevent currency change when ABA Routing Number is selected
    if (paymentType === paymentTypes.ABARoutingNumber) {
      setCurrency("USD");
      return;
    }
    const selectedCurrencyId = e.target.value;
    const selectedCurrency = availableCurrencyList.find(c => c.id == selectedCurrencyId);
    setCurrency(selectedCurrency?.name || "");
  };

  const onDeleteBankDataConfirmed = async () => {
    try {
      // Find all files associated with this bank data
      const files = fileList.filter(file => file.bankDataId === selectedBankData.id);

      for (const file of files) {
        try {
          await trackPromise(apiDeleteFile(token, file.fileName));
        } catch (error) {
          console.error(`Error deleting file ${file.fileName}:`, error);
        }
      }

      await trackPromise(apiDeleteBankData(token, selectedBankData.id));

      setBankDataList(
        bankDataList.filter((bank) => bank.id !== selectedBankData.id)
      );
      setFileList(
        fileList.filter((file) => file.bankDataId !== selectedBankData.id)
      );

      const numberOfFiles = files.length;
      const bankInfo = `${selectedBankData.type || "Unknown Type"}${selectedBankData.currency?.name ? ` (${selectedBankData.currency.name})` : ""}`;

      const message = numberOfFiles > 0
        ? `Bank detail ${bankInfo} and ${numberOfFiles} associated file${numberOfFiles > 1 ? 's' : ''} deleted successfully!`
        : `Bank detail ${bankInfo} deleted successfully!`;

      addAlert(message, "success");
    } catch (error) {
      console.error("Error deleting bank data:", error);
      addAlert(
        "Error deleting bank data. Please try again. " + error,
        "error"
      );
    }
  };

  const onDeleteBankData = async (bankData) => {
    setSelectedBankData(bankData);
    setToggleConfirmDelete(!toggleConfirmDelete);
  };

  const viewBankData = (bankData) => {
    setSelectedBankData(bankData);
    setViewModalOpen(true);
  };

  const setPrimaryBankData = async (bankDataId) => {
    try {
      const targetBank = bankDataList.find(bank => bank.id === bankDataId);
      if (!targetBank) {
        addAlert("Bank not found", "error");
        return;
      }

      // Check if there's already a primary bank for this currency
      const existingPrimary = bankDataList.find(
        bank => bank.currency?.id === targetBank.currency?.id &&
          bank.isPrimary &&
          bank.id !== bankDataId
      );

      if (existingPrimary) {
        const currencyName = targetBank.currency?.name || "this currency";
        addAlert(`There's already a primary bank account for the currency ${currencyName}.`, "error");
        return;
      }

      await trackPromise(apiBankDataSetPrimary(bankDataId, token));
      const accountResult = await trackPromise(apiGetRequest(token));
      setBankDataList(accountResult.data.bankDataList || []);

      const currencyName = targetBank.currency?.name || "the selected currency";
      addAlert(`Primary bank account for ${currencyName} set successfully!`, "success");
    } catch (error) {
      addAlert("Error setting primary bank data. Please try again. " + error, "error");
    }
  };


  const removePrimaryBank = async (bankDataId) => {
    try {
      const bankToDelete = bankDataList.find(bank => bank.id === bankDataId);
      if (!bankToDelete) {
        addAlert("Bank not found", "error");
        return;
      }

      await apiRemovePrimaryBank(bankDataId, token);
      const request = await apiGetRequest(token);
      setBankDataList(request.data.bankDataList || []);

      const currencyName = bankToDelete.currency?.name || "the selected currency";
      addAlert(`Primary status removed for ${currencyName} bank account successfully!`, "success");
    } catch (error) {
      console.error("Error removing primary bank data:", error);
      addAlert("Error removing primary bank data. Please try again. " + error, "error");
    }
  };

  const handleNext = () => {
    const anyBankDetails = Validate.anyBankDetails(bankDataList);
    if (!anyBankDetails.isValid) {
      addAlert(anyBankDetails.message, "error");
      return
    }
    navigate(`/${token}/files`);
  };

  const handleBack = () => {
    navigate(`/${token}/account`);
  };

  const sortedCurrencyList = availableCurrencyList.slice().sort((a, b) => {
    const orderMap = { USD: 0, SGD: 1, EUR: 2, DKK: 3 };
    return (orderMap[a.name] ?? Infinity) - (orderMap[b.name] ?? Infinity);
  });

  const vField = (label, value) => (
    <div className="vbdm__field">
      <div className="vbdm__field-label">{label}</div>
      <div className={`vbdm__field-value${!value ? ' vbdm__field-value--empty' : ''}`}>
        {value || '\u2014'}
      </div>
    </div>
  );

  return (
    <Container>
      <Row className="mb-4">
        <Col>
          <Alerts alerts={alerts} setAlerts={setAlerts} />
          <div className="vfp-header">
            <div className="vfp-header__step">Step 2 of 4</div>
            <h4 className="vfp-header__title">Bank Details</h4>
            <p className="vfp-header__sub">Add your bank account details. Please add one bank account for each currency. If your bank account accepts multiple currencies, please add each currency with same details separately.</p>
          </div>
          <div className="vfp-body">

            {requestLoaded && (
              <>
                <Card className="mb-3">
                  <CardBody>
                    <Form onSubmit={handleSubmit(onSubmit)}>


                      <div className="ptype__tabs" role="tablist">
                        <button type="button" role="tab"
                          className={`ptype__tab${paymentType === paymentTypes.IBAN ? ' ptype__tab--active' : ''}`}
                          onClick={() => { if (paymentType === paymentTypes.IBAN) { setPaymentType(null); resetFormFields(); } else { setPaymentType(paymentTypes.IBAN); setShowRegNo(false); resetFormFields(); } }}
                        >IBAN</button>
                        <button type="button" role="tab"
                          className={`ptype__tab${paymentType === paymentTypes.SWIFT ? ' ptype__tab--active' : ''}`}
                          onClick={() => { if (paymentType === paymentTypes.SWIFT) { setPaymentType(null); resetFormFields(); } else { setPaymentType(paymentTypes.SWIFT); resetFormFields(); if (!isFinanceAccount) { setShowRegNo(false); } else { setShowRegNo(true); } } }}
                        >Non-IBAN</button>
                        
                        {/* <button type="button" role="tab"
                          className={`ptype__tab${paymentType === paymentTypes.ABARoutingNumber ? ' ptype__tab--active' : ''}`}
                          onClick={() => { if (paymentType === paymentTypes.ABARoutingNumber) { setPaymentType(null); resetFormFields(); } else { setPaymentType(paymentTypes.ABARoutingNumber); setCurrency("USD"); setShowRegNo(false); resetFormFields(); } }}
                        >ABA Routing Number</button> */}
                      
                        <button type="button" role="tab"
                          className={`ptype__tab${paymentType === paymentTypes.REGNOACCOUNT ? ' ptype__tab--active' : ''}`}
                          onClick={() => { if (paymentType === paymentTypes.REGNOACCOUNT) { setPaymentType(null); resetFormFields(); } else { setPaymentType(paymentTypes.REGNOACCOUNT); setShowRegNo(true); resetFormFields(); } }}
                        >REGNO/ACCOUNT (DK)</button>
                        <button type="button" role="tab"
                          className={`ptype__tab${paymentType === paymentTypes.FIK ? ' ptype__tab--active' : ''}`}
                          onClick={() => { if (paymentType === paymentTypes.FIK) { setPaymentType(null); resetFormFields(); } else { setPaymentType(paymentTypes.FIK); setShowRegNo(false); resetFormFields(); } }}
                        >FIK (DK)</button>
                      </div>

                      {paymentType === paymentTypes.IBAN && (
                        <NewIbanForm
                          register={register}
                          errors={errors}
                          setError={setError}
                          clearErrors={clearErrors}
                          setValue={setValue}
                          enableSubmit={enableSubmit}
                          setEnableSubmit={setEnableSubmit}
                          isBankAccountNumberValid={isBankAccountNumberValid}
                          setIsBankAccountNumberValid={setIsBankAccountNumberValid}
                          isBICValid={isBICValid}
                          setIsBICValid={setIsBICValid}
                          isCorrespondentBICValid={isCorrespondentBICValid}
                          setIsCorrespondentBICValid={
                            setIsCorrespondentBICValid
                          }
                          token={token}
                          checkbox={paymentType}
                          onGetIBAN={onGetIBAN}
                          resetMetadata={resetMetadata}
                          handleAccountNo={handleAccountNo}
                          showRegNo={showRegNo}
                          beneficiaryName={beneficiaryNameIban}
                          onBeneficiaryNameChange={
                            onBeneficiaryNameChangeIban
                          }
                          isIbanValidated={isIbanValidated}
                          correspondentBicData={correspondentBicData}
                          isCorrespondentBicValidated={isCorrespondentBicValidated}
                          validatedCorrespondentBicCode={validatedCorrespondentBicCode}
                          isoCountryCode={countryCode}
                          getValues={getValues}
                          currencyList={availableCurrencyList}
                          sortedCurrencyList={sortedCurrencyList}
                          currency={currency}
                          onCurrencyChanged={onCurrencyChanged}
                          keepThisOnly={keepThisOnly}
                          onKeepThisOnlyChange={() =>
                            setKeepThisOnly(!keepThisOnly)
                          }
                        />
                      )}

                      {paymentType === paymentTypes.SWIFT && (
                        <NonIBANForm
                          register={register}
                          errors={errors}
                          setError={setError}
                          clearErrors={clearErrors}
                          setValue={setValue}
                          enableSubmit={enableSubmit}
                          setEnableSubmit={setEnableSubmit}
                          isBankAccountNumberValid={isBankAccountNumberValid}
                          setIsBankAccountNumberValid={setIsBankAccountNumberValid}
                          isBICValid={isBICValid}
                          setIsBICValid={setIsBICValid}
                          isCorrespondentBICValid={isCorrespondentBICValid}
                          setIsCorrespondentBICValid={
                            setIsCorrespondentBICValid
                          }
                          token={token}
                          showRegNo={showRegNo}
                          regNo={regNo}
                          setRegNo={setRegNo}
                          isRegNoValid={isRegNoValid}
                          setIsRegNoValid={setIsRegNoValid}
                          bankAccountNumber={bankAccountNumber}
                          accountNumberLength={accountNumberLength}
                          isFinanceAccount={isFinanceAccount}
                          beneficiaryName={beneficiaryNameSwift}
                          onBeneficiaryNameChange={
                            onBeneficiaryNameChangeSwift
                          }
                          handleAccountNo={handleAccountNo}
                          handleOnChangeRegistrationNo={
                            handleOnChangeRegistrationNo
                          }
                          onGetBIC={onGetBIC}
                          isBicValidated={isBicValidated}
                          correspondentBicData={correspondentBicData}
                          isCorrespondentBicValidated={isCorrespondentBicValidated}
                          validatedBicCode={validatedBicCode}
                          validatedCorrespondentBicCode={validatedCorrespondentBicCode}
                          isoCountryCode={countryCode}
                          getValues={getValues}
                          currencyList={availableCurrencyList}
                          sortedCurrencyList={sortedCurrencyList}
                          currency={currency}
                          onCurrencyChanged={onCurrencyChanged}
                          keepThisOnly={keepThisOnly}
                          onKeepThisOnlyChange={() =>
                            setKeepThisOnly(!keepThisOnly)
                          }
                          resetMetadata={resetMetadata}
                          isCorrespondentAbaValidated={isCorrespondentAbaValidated}
                          correspondentAbaData={correspondentAbaData}
                          validatedCorrespondentAbaCode={validatedCorrespondentAbaCode}
                          abaRoutingNumber={abaRoutingNumber}
                          onabaRoutingNumberChange={handleAbaRoutingNumberChange}
                        />
                      )}

                      {paymentType === paymentTypes.FIK && (
                        <FikForm
                          register={register}
                          errors={errors}
                          setError={setError}
                          clearErrors={clearErrors}
                          setValue={setValue}
                          enableSubmit={enableSubmit}
                          setEnableSubmit={setEnableSubmit}
                          token={token}
                          checkbox={paymentType}
                          handleKortartChange={handleKortartChange}
                          fikNumber={fikNumber}
                          handleOnChangeFik={handleOnChangeFik}
                        />
                      )}

                      {paymentType === paymentTypes.REGNOACCOUNT && (
                        <RegNoAccountForm
                          register={register}
                          errors={errors}
                          checkbox={paymentType}
                          regNo={regNo}
                          setRegNo={setRegNo}
                          isRegNoValid={isRegNoValid}
                          setIsRegNoValid={setIsRegNoValid}
                          handleOnChangeRegistrationNo={
                            handleOnChangeRegistrationNo
                          }
                          bankAccountNumber={bankAccountNumber}
                          handleAccountNo={handleAccountNo}
                          isBankAccountNumberValid={isBankAccountNumberValid}
                          setIsBankAccountNumberValid={setIsBankAccountNumberValid}
                          isRegNoAccountValidated={isRegNoAccountValidated}
                          getValues={getValues}
                        />
                      )}

                      {paymentType === paymentTypes.ABARoutingNumber && (
                        <AbaRoutingNumberForm
                          register={register}
                          errors={errors}
                          setValue={setValue}
                          clearErrors={clearErrors}
                          resetMetadata={resetMetadata}
                          isAbaRoutingNumberValidated={isAbaRoutingNumberValidated}
                          isCorrespondentAbaValidated={isCorrespondentAbaValidated}
                          correspondentAbaData={correspondentAbaData}
                          validatedCorrespondentAbaCode={validatedCorrespondentAbaCode}
                          getValues={getValues}
                          currencyList={availableCurrencyList}
                          sortedCurrencyList={sortedCurrencyList}
                          allCurrencyList={allCurrencyList}
                          currency={currency}
                          onCurrencyChanged={onCurrencyChanged}
                        />
                      )}

                      <Row>
                        <Col sm="12">
                          <div className="d-flex gap-2 mt-3">
                            {paymentType === paymentTypes.IBAN &&
                              !isIbanValidated && (
                                <Button
                                  className="btn-add-bank-data"
                                  type="button"
                                  onClick={onGetIBAN}
                                >
                                  Validate IBAN
                                </Button>
                              )}
                            {paymentType === paymentTypes.SWIFT &&
                              !isBicValidated && (
                                <Button
                                  className="btn-add-bank-data"
                                  type="button"
                                  onClick={onGetBIC}
                                >
                                  Validate SWIFT BIC
                                </Button>
                              )}
                            {paymentType === paymentTypes.ABARoutingNumber &&
                              !isAbaRoutingNumberValidated && (
                                <Button
                                  className="btn-add-bank-data"
                                  type="button"
                                  onClick={onGetABARoutingNumber}
                                >
                                  Validate ABA Routing Number
                                </Button>
                              )}
                            {paymentType === paymentTypes.REGNOACCOUNT &&
                              !isRegNoAccountValidated && (
                                <Button
                                  className="btn-add-bank-data"
                                  type="button"
                                  onClick={onCalculateIBANFromRegNo}
                                >
                                  Validate
                                </Button>
                              )}
                            {paymentType &&
                              ((paymentType === paymentTypes.SWIFT &&
                                isBicValidated) ||
                                (paymentType === paymentTypes.IBAN &&
                                  isIbanValidated) ||
                                (paymentType === paymentTypes.ABARoutingNumber &&
                                  isAbaRoutingNumberValidated) ||
                                (paymentType === paymentTypes.REGNOACCOUNT &&
                                  isRegNoAccountValidated) ||
                                paymentType === paymentTypes.FIK) && (
                                <Button
                                  className="btn-add-bank-data"
                                  type="submit"
                                  disabled={!enableSubmit}
                                >
                                  Add Bank Data
                                </Button>
                              )}
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  </CardBody>
                </Card>

                {bankDataList.length > 0 && (
                  <div className="bdc__section">
                    <p className="bdc__section-label">Added bank details</p>
                    <div className="bdc__grid">
                      {bankDataList.map((bankData, index) => (
                        <div
                          key={index}
                          className={`bdc__card${bankData.isPrimary ? ' bdc__card--primary' : ''}`}
                          onClick={() => viewBankData(bankData)}
                        >
                          <div className="bdc__card-header">
                            <div className="bdc__bank-name">{bankData.bank || bankData.type || "N/A"}</div>
                          </div>
                          <div className="bdc__meta">
                            <span className="bdc__meta-item">{bankData.type || "N/A"}</span>
                            <span className="bdc__meta-sep">·</span>
                            <span className="bdc__meta-item">{bankData.currency?.name || "N/A"}</span>
                          </div>
                          <div className="bdc__identifier">
                            {bankData.type === paymentTypes.IBAN && (bankData.iban || "—")}
                            {bankData.type === paymentTypes.SWIFT && (bankData.bic || "—")}
                            {bankData.type === paymentTypes.FIK && (bankData.fikNumber ? bankData.fikNumber.toString().replace(/^\+0?/, '') : "—")}
                            {bankData.type === paymentTypes.REGNOACCOUNT && (bankData.bankAccountNumber || "—")}
                            {bankData.type === paymentTypes.ABARoutingNumber && (bankData.abaRoutingNumber || "—")}
                          </div>
                          <div className="bdc__country">{bankData.country || "N/A"}{bankData.countryCode ? ` (${bankData.countryCode})` : ''}</div>
                          <div className="bdc__actions" onClick={e => e.stopPropagation()}>
                            <FontAwesomeIcon
                              icon={faCheck}
                              className={`bdc__action-icon${bankData.isPrimary ? ' bdc__action-icon--active' : ''}`}
                              onClick={bankData.isPrimary ? () => removePrimaryBank(bankData.id) : () => setPrimaryBankData(bankData.id)}
                              data-tooltip-id="primary-tooltip"
                              data-tooltip-content={bankData.isPrimary ? `Remove primary status for ${bankData.currency?.name || 'this currency'}` : `Set as primary bank for ${bankData.currency?.name || 'this currency'}`}
                            />
                            <FontAwesomeIcon
                              icon={faEye}
                              className="bdc__action-icon"
                              onClick={() => viewBankData(bankData)}
                              data-tooltip-id="view-tooltip"
                              data-tooltip-content="View details"
                            />
                            <FontAwesomeIcon
                              icon={faTrash}
                              className="bdc__action-icon bdc__action-icon--delete"
                              onClick={() => onDeleteBankData(bankData)}
                              data-tooltip-id="delete-tooltip"
                              data-tooltip-content="Delete"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="mt-4 d-flex justify-content-between">
              <Button className="btn-back" onClick={handleBack}>
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Back: Account Info
              </Button>
              <Button className="btn-next" onClick={handleNext}>
                Next: Upload Files
                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <Modal isOpen={viewModalOpen} toggle={() => setViewModalOpen(false)} className="vbdm">
        <div className="vbdm__header">
          <div className="vbdm__header-left">
            <div className="vbdm__title">Bank Details</div>
          </div>
          <button className="vbdm__close" onClick={() => setViewModalOpen(false)} aria-label="Close">&times;</button>
        </div>
        <div className="vbdm__body">

          {selectedBankData.type === paymentTypes.FIK && (
            <div className="vbdm__section">
              <div className="vbdm__grid">
                {vField("Kortart", selectedBankData.fikKortart)}
                {vField("Kreditor/GIRO", selectedBankData.fikNumber ? selectedBankData.fikNumber.toString().replace(/^\+0?/, '') : null)}
              </div>
            </div>
          )}

          {selectedBankData.type === paymentTypes.REGNOACCOUNT && (
            <div className="vbdm__section">
              <div className="vbdm__grid">
                {vField("Registration Number", selectedBankData.regNo)}
                {vField("Account Number", selectedBankData.bankAccountNumber)}
                {vField("Currency", selectedBankData.currency?.name)}
              </div>
            </div>
          )}

          {selectedBankData.type === paymentTypes.ABARoutingNumber && (
            <>
              <div className="vbdm__section">
                <div className="vbdm__section-label">Account</div>
                <div className="vbdm__grid">
                  {vField("Account Number", selectedBankData.bankAccountNumber)}
                  {vField("Beneficiary Name", selectedBankData.beneficiaryBankAccountName)}
                  {vField("Currency", selectedBankData.currency?.name)}
                </div>
              </div>
              <div className="vbdm__divider" />
              <div className="vbdm__section">
                <div className="vbdm__grid">
                  {vField("Beneficiary Bank ABA Routing Number", selectedBankData.abaRoutingNumber)}
                  {vField("Beneficiary Bank Name", selectedBankData.bank)}
                  {vField("Beneficiary Bank Address", selectedBankData.address)}
                  {vField("Beneficiary Bank City", selectedBankData.city)}
                  {vField("Beneficiary Bank Postal Code", selectedBankData.zip)}
                  {vField("Beneficiary Bank Country Name", selectedBankData.country)}
                </div>
              </div>
              {selectedBankData.correspondentBankABARoutingNumber && (
                <>
                  <div className="vbdm__divider" />
                  <div className="vbdm__section">
                    <div className="vbdm__grid">
                      {vField("Correspondent Bank ABA Routing Number", selectedBankData.correspondentBankABARoutingNumber)}
                      {vField("Correspondent Bank Name", selectedBankData.correspondentBankName)}
                      {vField("Correspondent Bank Address", selectedBankData.correspondentBankAddress)}
                      {vField("Correspondent Bank City", selectedBankData.correspondentBankCity)}
                      {vField("Correspondent Bank Postal Code", selectedBankData.correspondentBankPostalCode)}
                      {vField("Correspondent Bank Country Name", selectedBankData.correspondentBankCountryName)}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {selectedBankData.type === paymentTypes.IBAN && (
            <>
              <div className="vbdm__section">
                <div className="vbdm__section-label">Account</div>
                <div className="vbdm__grid">
                  {vField("Beneficiary Name", selectedBankData.beneficiaryBankAccountName)}
                  {vField("IBAN", selectedBankData.iban)}
                  {vField("Currency", selectedBankData.currency?.name)}
                </div>
              </div>
              <div className="vbdm__divider" />
              <div className="vbdm__section">
                <div className="vbdm__grid">
                  {vField("Beneficiary Bank Name", selectedBankData.bank)}
                  {vField("Beneficiary Bank Address", selectedBankData.address)}
                  {vField("Beneficiary Bank City", selectedBankData.city)}
                  {vField("Beneficiary Bank Postal Code", selectedBankData.zip)}
                  {vField("Beneficiary Bank Country Name", selectedBankData.country)}
                  {vField("Beneficiary Bank Country Code", selectedBankData.countryCode)}
                </div>
              </div>
              {selectedBankData.correspondentBankSwift && (
                <>
                  <div className="vbdm__divider" />
                  <div className="vbdm__section">
                    <div className="vbdm__grid">
                      {vField("Correspondent Bank SWIFT", selectedBankData.correspondentBankSwift)}
                      {vField("Correspondent Bank Name", selectedBankData.correspondentBankName)}
                      {vField("Correspondent Bank Address", selectedBankData.correspondentBankAddress)}
                      {vField("Correspondent Bank City", selectedBankData.correspondentBankCity)}
                      {vField("Correspondent Bank Postal Code", selectedBankData.correspondentBankPostalCode)}
                      {vField("Correspondent Bank Country Name", selectedBankData.correspondentBankCountryName)}
                      {vField("Correspondent Bank Country Code", selectedBankData.correspondentBankCountryCode)}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {selectedBankData.type === paymentTypes.SWIFT && (
            <>
              <div className="vbdm__section">
                <div className="vbdm__section-label">Account</div>
                <div className="vbdm__grid">
                  {vField("Beneficiary Name", selectedBankData.beneficiaryBankAccountName)}
                  {vField("Account Number", selectedBankData.bankAccountNumber)}
                  {vField("Currency", selectedBankData.currency?.name)}
                </div>
              </div>
              <div className="vbdm__divider" />
              <div className="vbdm__section">
                <div className="vbdm__grid">
                  {vField("Beneficiary Bank SWIFT", selectedBankData.bic)}
                  {vField("Beneficiary Bank Name", selectedBankData.bank)}
                  {vField("Beneficiary Bank Branch", selectedBankData.branch)}
                  {vField("Beneficiary Bank Address", selectedBankData.address)}
                  {vField("Beneficiary Bank City", selectedBankData.city)}
                  {vField("Beneficiary Bank Postal Code", selectedBankData.zip)}
                  {vField("Beneficiary Bank Country Name", selectedBankData.country)}
                  {vField("Beneficiary Bank Country Code", selectedBankData.countryCode)}
                </div>
              </div>
              {selectedBankData.correspondentBankSwift && (
                <>
                  <div className="vbdm__divider" />
                  <div className="vbdm__section">
                    <div className="vbdm__grid">
                      {vField("Correspondent Bank SWIFT", selectedBankData.correspondentBankSwift)}
                      {vField("Correspondent Bank Name", selectedBankData.correspondentBankName)}
                      {vField("Correspondent Bank Address", selectedBankData.correspondentBankAddress)}
                      {vField("Correspondent Bank City", selectedBankData.correspondentBankCity)}
                      {vField("Correspondent Bank Postal Code", selectedBankData.correspondentBankPostalCode)}
                      {vField("Correspondent Bank Country Name", selectedBankData.correspondentBankCountryName)}
                      {vField("Correspondent Bank Country Code", selectedBankData.correspondentBankCountryCode)}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

        </div>
      </Modal>

      <Confirm
        onConfirm={onDeleteBankDataConfirmed}
        text="Are you sure you want to delete this?"
        toggle={toggleConfirmDelete}
        setToggle={setToggleConfirmDelete}
      />

      {/* Tooltip components */}
      <Tooltip id="primary-tooltip" />
      <Tooltip id="view-tooltip" />
      <Tooltip id="delete-tooltip" />
    </Container>
  );
};

export default BankDetailsPage;
